import PropTypes from "prop-types";

import { Box, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  formatMoney,
  formatNumber,
  lensProductAvailabilityLabels,
} from "@/features/shared";
import { DownstreamTransactionLinks } from "@/features/ui";

const LensProductItem = ({
  index,
  lensProduct: {
    name,
    availability,
    discount,
    note,
    price,
    salesOrderLensProductAddOns,
    salesOrderLensProductVariants,
  },
}) => {
  const { hasPermission } = useAuth();

  const columns = [
    {
      field: "side",
      headerName: "Sisi",
      flex: 1,
      sortable: false,
    },
    {
      field: "sku",
      headerName: "SKU",
      flex: 3,
      sortable: false,
    },
    {
      field: "variant",
      headerName: "Varian",
      flex: 2,
      sortable: false,
      valueGetter: (_, row) => {
        // Get axis from note
        let axis = "-";
        try {
          const parsed = JSON.parse(row.note || "{}");
          axis = parsed.axis || "0";
        } catch {
          // Ignore JSON parse errors
        }

        // Get sph, cyl, add from variant attributes
        const sph = row.variantAttribute1Value || "-";
        const cyl = row.variantAttribute2Value || "-";
        const add = row.variantAttribute3Value || "0"; // Use "0" if null

        // Build prescription format: {sph}/{cyl}/{axis}/{add}
        return `${sph}/${cyl}/${axis}/${add}`;
      },
    },
    {
      field: "variantNote",
      headerName: "PD/Prism",
      flex: 1.5,
      sortable: false,
      valueGetter: (_, row) => {
        let pd = "-",
          prism = "-";

        try {
          const parsed = JSON.parse(row.note || "{}");
          pd = parsed.pd || "-";
          prism = parsed.prism || "-";
        } catch {
          // Ignore JSON parse errors
        }

        // Build format: {pd}/{prism}
        return `${pd}/${prism}`;
      },
    },
    {
      field: "deliveryOrders",
      headerName: "Surat Jalan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.deliveryOrders}
          clickable={hasPermission(PermissionEnum.READ_DELIVERY_ORDERS)}
        />
      ),
    },
    {
      field: "salesInvoices",
      headerName: "Faktur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.salesInvoices}
          clickable={hasPermission(PermissionEnum.READ_SALES_INVOICES)}
        />
      ),
    },
    {
      field: "salesPayments",
      headerName: "Pembayaran",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.salesPayments}
          clickable={hasPermission(PermissionEnum.READ_SALES_PAYMENTS)}
        />
      ),
    },
    {
      field: "price",
      headerName: "Harga",
      flex: 1,
      type: "number",
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between"
          }}>
          <Stack direction="row" sx={{
            alignItems: "center"
          }}>
            <Typography variant="body2">
              {index + 1}. {name} ({lensProductAvailabilityLabels[availability]}
              )
            </Typography>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <DataGrid
          getRowHeight={() => "auto"}
          columns={columns}
          rows={salesOrderLensProductVariants}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          hideFooter
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography variant="body2">
            <span style={{ color: "gray" }}>Note: </span>
            {note ?? "-"}
          </Typography>
          <Box>
            {salesOrderLensProductAddOns.length > 0 && (
              <Typography align="right" variant="body2">
                {salesOrderLensProductAddOns[0].name} (+
                {formatMoney(salesOrderLensProductAddOns[0].price)})
              </Typography>
            )}
            <Stack
              direction="row"
              sx={{
                alignItems: "baseline",
                justifyContent: "right"
              }}>
              {discount > 0 && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: "medium",
                    textDecoration: "line-through",
                    mr: 0.5,
                    color: "grey",
                  }}
                >
                  {formatNumber(price)}
                </Typography>
              )}
              <Typography variant="body2" color={"primary"} sx={{
                fontWeight: 600
              }}>
                {formatMoney(price - discount)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default LensProductItem;

LensProductItem.propTypes = {
  index: PropTypes.number.isRequired,
  lensProduct: PropTypes.object.isRequired,
};
